import React from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpHubPage = () => {
  const helpCategories = [
    {
      title: 'System Overview',
      description: 'Get started with the SEN Curriculum Management System',
      IconComponent: Icons.Home,
      color: 'blue',
      link: '/help/dashboard',
      features: ['System overview', 'Key features', 'Navigation guide', 'Getting started']
    },
    {
      title: 'Student Management',
      description: 'Learn how to manage student profiles and SEN information',
      IconComponent: Icons.Users,
      color: 'green',
      link: '/help/student-management',
      features: ['Adding students', 'Managing profiles', 'EHCP integration', 'Best practices']
    },
    {
      title: 'Curriculum Generation',
      description: 'Create personalized curricula using AI-powered tools',
      IconComponent: Icons.BookOpen,
      color: 'purple',
      link: '/help/curriculum-generation',
      features: ['Creating curricula', 'Assessment frameworks', 'Personalization', 'Managing curricula']
    },
    {
      title: 'Progress Tracking',
      description: 'Monitor student development and achievements',
      IconComponent: Icons.ChartBar,
      color: 'indigo',
      link: '/help/progress-tracking',
      features: ['Progress dashboard', 'Tracking methods', 'Reports & analytics', 'Best practices']
    },
    {
      title: 'Term Planning',
      description: 'Create and manage academic term plans',
      IconComponent: Icons.Calendar,
      color: 'orange',
      link: '/help/term-planning',
      features: ['Creating plans', 'Planning structure', 'Implementation', 'Best practices']
    },
    {
      title: 'Intervention System',
      description: 'Track student interventions and progress',
      IconComponent: Icons.Target,
      color: 'red',
      link: '/intervention-demo',
      features: ['System overview', 'Key features', 'How to use', 'Live demo']
    },
    {
      title: 'Graduated Response Reports',
      description: 'Generate comprehensive SEN support reports',
      IconComponent: Icons.ClipboardDocumentList,
      color: 'teal',
      link: '/help/graduated-response',
      features: ['What is GR', 'Generating reports', 'Using reports', 'Best practices']
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
      green: 'bg-green-50 border-green-200 hover:bg-green-100',
      purple: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
      indigo: 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100',
      orange: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
      red: 'bg-red-50 border-red-200 hover:bg-red-100',
      teal: 'bg-teal-50 border-teal-200 hover:bg-teal-100'
    };
    return colorMap[color] || 'bg-gray-50 border-gray-200 hover:bg-gray-100';
  };

  const getIconColor = (color) => {
    const colorMap = {
      blue: 'text-blue-600',
      green: 'text-green-600',
      purple: 'text-purple-600',
      indigo: 'text-indigo-600',
      orange: 'text-orange-600',
      red: 'text-red-600',
      teal: 'text-teal-600'
    };
    return colorMap[color] || 'text-gray-600';
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center"><Icons.BookOpen className="w-10 h-10 mr-3" /> Help & Support Hub</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Welcome to the comprehensive help system for the SEN Curriculum Management System. 
              Find detailed guides, tutorials, and best practices for all features.
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center"><Icons.Rocket className="w-6 h-6 mr-2" /> Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/dashboard"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <Icons.Home className="w-5 h-5 mr-2" /> Go to Dashboard
            </Link>
            <Link
              to="/student-organization"
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Icons.Users className="w-5 h-5 mr-2" /> Manage Students
            </Link>
            <Link
              to="/generate-curriculum"
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <Icons.BookOpen className="w-5 h-5 mr-2" /> Generate Curriculum
            </Link>
            <Link
              to="/interventions"
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center"
            >
              <Icons.Target className="w-5 h-5 mr-2" /> Track Interventions
            </Link>
          </div>
        </div>

        {/* Help Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {helpCategories.map((category, index) => (
            <div
              key={index}
              className={`border-2 rounded-lg p-6 transition-all duration-200 ${getColorClasses(category.color)}`}
            >
              <div className="text-center mb-4">
                <div className={`flex justify-center mb-2 ${getIconColor(category.color)}`}>
                  <category.IconComponent className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{category.title}</h3>
                <p className="text-gray-600 text-sm">{category.description}</p>
              </div>
              
              <div className="mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">What you'll learn:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {category.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Icons.Check className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to={category.link}
                className={`block w-full text-center py-3 px-4 rounded-lg font-medium transition-colors ${
                  category.color === 'blue' ? 'bg-blue-600 text-white hover:bg-blue-700' :
                  category.color === 'green' ? 'bg-green-600 text-white hover:bg-green-700' :
                  category.color === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-700' :
                  category.color === 'indigo' ? 'bg-indigo-600 text-white hover:bg-indigo-700' :
                  category.color === 'orange' ? 'bg-orange-600 text-white hover:bg-orange-700' :
                  category.color === 'red' ? 'bg-red-600 text-white hover:bg-red-700' :
                  category.color === 'teal' ? 'bg-teal-600 text-white hover:bg-teal-700' :
                  'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                Get Started →
              </Link>
            </div>
          ))}
        </div>

        {/* Additional Resources */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center"><Icons.BookOpen className="w-6 h-6 mr-2" /> Additional Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Tips & Tricks</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Start with the System Overview if you're new</li>
                <li>• Use the search function to find specific information</li>
                <li>• Bookmark frequently used help pages</li>
                <li>• Follow the step-by-step guides for best results</li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Wrench className="w-5 h-5 mr-2" /> Troubleshooting</h3>
              <ul className="text-gray-600 space-y-1">
                <li>• Check that you're logged in properly</li>
                <li>• Ensure you have the necessary permissions</li>
                <li>• Clear your browser cache if needed</li>
                <li>• Contact support for technical issues</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="bg-blue-50 p-6 rounded-lg mt-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-blue-900 mb-2">Need More Help?</h2>
            <p className="text-blue-800 mb-4">
              If you can't find what you're looking for or need additional support, 
              don't hesitate to reach out to our team.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                <Icons.Envelope className="w-5 h-5 mr-2" /> Contact Support
              </button>
              <button className="px-6 py-3 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors flex items-center">
                <Icons.ClipboardDocumentList className="w-5 h-5 mr-2" /> Request Feature
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpHubPage; 